import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Task } from 'src/careflow/ph-core/fhir-client/fhir-base/mappings/Task';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { TaskWrapper, WrappedTask } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Task';
import { fhirClient } from 'src/App';
import { CustomModalBasicProps } from 'src/components/CustomModal';
import useAuth from 'src/careflow/ph-core/authentication/hook/useAuth';
import useCheckNTaskLinker from 'src/hooks/useCheckNTaskLinker';
import useTranslations from 'src/careflow/ph-core/internationalization/hook/useTranslations';
import { getAssessmentFromTask, getAssessmentUrlWithUserId } from 'src/careflow/helpers/common';
import AssessmentFrame from 'src/careflow/care-coordination/patient/assessment/frame/AssessmentFrame';
import { INTERVIEW_ACTIVE_STATUSES, INTERVIEW_CANCELED_STATUSES } from './constants';
import { mapFhirResourceReference } from 'src/careflow/ph-core/workflow/helpers/workflow';
import { useRevisitTaskValidation } from 'src/hooks/useRevisitTaskValidation';
import { useStandardLoadingBackdrop } from 'src/careflow/ph-core/ui/base/StandardLoadingBackdropProvider';
import { Reference } from 'src/careflow/ph-core/fhir-client/resources';
import { wait } from 'src/utils/timers';

const VERIFY_POLL_MAX_ATTEMPTS = 20;
const VERIFY_POLL_INTERVAL_MS = 3000;
const VERIFY_SUBMIT_CHECK_ATTEMPTS = 8;
const VERIFY_SUBMIT_CHECK_INTERVAL_MS = 1500;

const getTaskVersion = (task: any): string | undefined =>
  task?.meta?.versionId ?? task?.meta?.lastUpdated;

interface Props extends CustomModalBasicProps {
  patient: WrappedPatient | null;
  open: boolean;
  displayStartRelatedAssessmentFirst?: boolean;
  handlerSave: (data: any) => void;
  onClose: () => void;
  checkListData: any;
  basedOn?: Reference;
}

export default function AssessmentFromChecklistData({
  patient,
  open,
  displayStartRelatedAssessmentFirst,
  onClose,
  handlerSave,
  checkListData,
  basedOn,
}: Props) {
  const translate = useTranslations(`assessmentDialog`);
  const authUser = useAuth().getCurrentUser();

  const isRevisitTask = checkListData?.code?.includes('REVISIT_UNANSWERED_NEEDS');
  const revisitValidation = useRevisitTaskValidation(
    isRevisitTask ? patient?.id || null : null,
    checkListData
  );

  const activityDefinitionId: string | null = useMemo(() => {
    if (!checkListData) {
      return null;
    }

    try {
      const metadata = JSON.parse(checkListData.metadata);
      const { activityDefinitionRef } = metadata.ui.data;
      const activityDefinitionId = activityDefinitionRef.split('/')[1];

      if (typeof activityDefinitionId === 'string') {
        return activityDefinitionId;
      }
    } catch (err) {
      return null;
    }

    return null;
  }, [checkListData]);

  const metadata = useMemo(() => {
    if (!checkListData) {
      return null;
    }

    try {
      const metadata = JSON.parse(checkListData.metadata);
      const {
        errorMessage = null,
        createTask,
        filterLast = false,
        allowMany = false,
      } = metadata?.ui?.data ?? {};

      return {
        errorMessage:
          typeof errorMessage === 'string'
            ? errorMessage
            : translate('notReadyTryAgainLaterMessage'),
        createTask,
        filterLast,
        allowMany,
      };
    } catch (err) {
      return {
        errorMessage: translate('notReadyTryAgainLaterMessage'),
        createTask: undefined,
        filterLast: false,
        allowMany: false,
      };
    }
  }, [checkListData]);

  const patientId = useMemo(() => patient?.id || '', [patient]);

  const standardLoadingBackdrop = useStandardLoadingBackdrop();
  const { enqueueSnackbar } = useSnackbar();

  const taskLinker = useCheckNTaskLinker({ patientId, activityDefinitionId: activityDefinitionId });

  const [assessmentUrl, setAssessmentUrl] = useState<string>();

  // Set once the dialog has driven its opening flow, so later re-runs of the effect below (the
  // revisit lookup resolving, a re-render) can never restart it or tear down a survey in progress.
  const initializedRef = useRef(false);

  const handlerSaveRef = useRef(handlerSave);
  handlerSaveRef.current = handlerSave;

  const verifyTargetKey = `${patientId}|${checkListData?.id ?? checkListData?.code ?? ''}`;
  const verifyTargetKeyRef = useRef(verifyTargetKey);
  verifyTargetKeyRef.current = verifyTargetKey;

  const verifyRunRef = useRef(0);

  const openedTaskRef = useRef<{ id?: string; version?: string } | null>(null);

  const surveyPageLoadsRef = useRef(0);

  const standardLoadingBackdropRef = useRef(standardLoadingBackdrop);
  standardLoadingBackdropRef.current = standardLoadingBackdrop;
  const verifyLoaderOpenRef = useRef(false);

  async function findAssessmentTask(subjectId: string, activityDefinitionId: string) {
    const statusQuery = INTERVIEW_ACTIVE_STATUSES.join(',');
    let query = `/Task?code=assessment&subject=${subjectId}&status=${statusQuery}`;
    let taskQueryRes;

    try {
      taskQueryRes = await fhirClient.search(query);
    } catch (error) {
      console.error(error);
      throw new Error('failed to get Task data to get assessment');
    }

    if (!taskQueryRes.entry || taskQueryRes.entry.length < 1) {
      return undefined;
    }

    const expectedInstantiatesCanonical = 'ActivityDefinition/' + activityDefinitionId;

    const assessmentTasks = taskQueryRes.entry
      .map(({ resource }) => resource as Task)
      .filter(
        (task) =>
          task &&
          task.instantiatesCanonical === expectedInstantiatesCanonical &&
          task.code?.coding?.some((coding) => coding?.code?.includes?.('assessment'))
      );

    if (!assessmentTasks.length) {
      return undefined;
    }

    if (metadata?.filterLast) {
      return assessmentTasks.reduce((latest, current) =>
        new Date(current?.lastModified || current.meta?.lastUpdated || 0).getTime() >
        new Date(latest?.lastModified || latest.meta?.lastUpdated || 0).getTime()
          ? current
          : latest
      );
    }

    return TaskWrapper(assessmentTasks[0]);
  }

  async function setAssessmentUrlFromTask(task: Task) {
    const inputAssessment = getAssessmentFromTask(task);

    if (!inputAssessment || !inputAssessment.valueUrl) {
      if (isRevisitTask) {
        try {
          const completedTasksResponse = await fhirClient.search(`/Task?subject=${patientId}&code=assessment&status=completed&_count=10`) as any;
          const allTasks = completedTasksResponse?.entry?.map((entry: any) => entry?.resource) || [];
          const initialTask = allTasks.find((t: Task) => t?.instantiatesCanonical?.includes('INITIAL-SCREENING'));
          
          if (initialTask) {
            const initialInput = getAssessmentFromTask(initialTask);
            
            if (initialInput?.valueUrl) {
              const baseUrl = new URL(initialInput.valueUrl);
              
              baseUrl.searchParams.set('taskUUID', task.id || '');
              baseUrl.searchParams.set('patientUUID', patientId || '');
              baseUrl.searchParams.set('newtest', 'Y');
              baseUrl.searchParams.set('lang', 'en');
              
              const revisitAssessmentUrl = baseUrl.toString();
              
              const assessmentUrlWithUserId = getAssessmentUrlWithUserId(
                { valueUrl: revisitAssessmentUrl } as any,
                authUser.user_fhir_uri
              );
              
              standardLoadingBackdrop.close();
              setAssessmentUrl(assessmentUrlWithUserId);
              return;
            }
          }
        } catch (error) {
          console.error('Failed to generate revisit assessment URL:', error);
        }
      }
      
      enqueueSnackbar(translate('notReadyContactAdminMessage'), {
        variant: 'error',
      });
      handleClose(false);
      return;
    }

    if (!inputAssessment.valueUrl.includes('token=')) {
      enqueueSnackbar(metadata?.errorMessage, {
        variant: 'error',
      });
      handleClose(false);
      return;
    }

    const assessmentUrlWithUserId = getAssessmentUrlWithUserId(
      inputAssessment,
      authUser.user_fhir_uri,
      { taskId: task?.id, patientId: patient?.id }
    );

    openedTaskRef.current = { id: task?.id, version: getTaskVersion(task) };
    surveyPageLoadsRef.current = 0;

    standardLoadingBackdrop.close();

    setAssessmentUrl(assessmentUrlWithUserId);
  }

  function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms || 300));
  }

  async function createTask(activityDefinitionId: string) {
    const response = await taskLinker.createAssessmentTask(activityDefinitionId, null, basedOn);

    if (!response) {
      enqueueSnackbar('An error has occurred', {
        variant: 'error',
      });
      handleClose(false);
      throw new Error('failed to get Assessment Task data');
    }

    enqueueSnackbar(translate('preparingWaitMessage'), {
      variant: 'info',
    });

    await sleep(12000);

    const assessmentTask = await findAssessmentTask(patientId, activityDefinitionId);

    return assessmentTask ? TaskWrapper(assessmentTask) : undefined;
  }

  async function initialHandler(patientId: string, activityDefinitionId: string) {
    // For revisit tasks, use the existing revisit task instead of searching by activityDefinitionId
    let assessmentTask =
      isRevisitTask && revisitValidation.latestTask
        ? revisitValidation.latestTask
        : await findAssessmentTask(patientId, activityDefinitionId);

    if (displayStartRelatedAssessmentFirst && !assessmentTask) {
      enqueueSnackbar(translate('startRelatedAssessmentFirst'), {
        variant: 'error',
      });
      handleClose(false);
      return;
    }

    const shouldCreateTask = !assessmentTask && !isRevisitTask && metadata?.createTask !== false;

    if (shouldCreateTask) {
      assessmentTask = await createTask(activityDefinitionId);
    }

    const aTaskWrapper = TaskWrapper(assessmentTask);

    const shouldCreateAdditionalTask =
      assessmentTask && assessmentTask.status && !aTaskWrapper.isActive() && metadata?.allowMany;

    if (shouldCreateAdditionalTask) {
      assessmentTask = await createTask(activityDefinitionId);
    }

    if (assessmentTask && assessmentTask.status && !aTaskWrapper.isActive()) {
      enqueueSnackbar(translate('doneMessage'), { variant: 'info' });

      handleClose(true, assessmentTask);
      return;
    }

    setAssessmentUrlFromTask(assessmentTask);
  }

  const isTaskCanceled = (task: any): boolean =>
    !!task?.status && INTERVIEW_CANCELED_STATUSES.includes(task.status);

  const completeStepFromTask = (task: any) =>
    handlerSaveRef.current({
      fhirReference: mapFhirResourceReference(task),
      instantiatesCanonical: task.instantiatesCanonical,
    });

  const isTaskUpdatedSinceOpened = (task: any): boolean => {
    const opened = openedTaskRef.current;

    return !!opened && opened.id === task?.id && opened.version !== getTaskVersion(task);
  };

  async function verifyHandler(
    patientId: string,
    activityDefinitionId: string,
    loaderAlreadyShown: boolean
  ) {
    const run = ++verifyRunRef.current;
    const targetKey = verifyTargetKeyRef.current;
    const isSuperseded = () =>
      verifyRunRef.current !== run || verifyTargetKeyRef.current !== targetKey;

    let submitted = false;
    let loaderShown = loaderAlreadyShown;
    let quickChecks = 0;

    const markSubmitted = () => {
      submitted = true;

      if (!loaderShown) {
        loaderShown = true;
        verifyLoaderOpenRef.current = true;
        standardLoadingBackdrop.open();
      }
    };

    for (let attempt = 0; attempt < VERIFY_POLL_MAX_ATTEMPTS; ) {
      let assessmentTask: any;

      try {
        assessmentTask = await findAssessmentTask(patientId, activityDefinitionId);
      } catch (error) {
        assessmentTask = undefined;
      }

      if (isSuperseded()) return;

      if (assessmentTask) {
        if (isTaskCanceled(assessmentTask)) break;

        if (assessmentTask.status && !TaskWrapper(assessmentTask).isActive()) {
          markSubmitted();
          await completeStepFromTask(assessmentTask);
          break;
        }

        if (isTaskUpdatedSinceOpened(assessmentTask)) {
          markSubmitted();
        }
      }

      if (submitted) {
        attempt++;

        if (attempt === VERIFY_POLL_MAX_ATTEMPTS) {
          enqueueSnackbar(translate('preparingWaitMessage'), { variant: 'info' });
          break;
        }
      } else if (++quickChecks === VERIFY_SUBMIT_CHECK_ATTEMPTS) {
        break;
      }

      await wait(submitted ? VERIFY_POLL_INTERVAL_MS : VERIFY_SUBMIT_CHECK_INTERVAL_MS);

      if (isSuperseded()) return;
    }

    if (loaderShown && !isSuperseded()) {
      verifyLoaderOpenRef.current = false;
      standardLoadingBackdrop.close();
    }
  }

  async function handleClose(verify: boolean = true, task?: WrappedTask) {
    setAssessmentUrl(undefined);

    if (verify && task && !isTaskCanceled(task) && task.status && !TaskWrapper(task).isActive()) {
      await completeStepFromTask(task);
    }

    verifyRunRef.current++;

    const shouldVerify = !!patientId && !!activityDefinitionId && verify && !task;
    const likelySubmitted = shouldVerify && surveyPageLoadsRef.current > 1;

    verifyLoaderOpenRef.current = likelySubmitted;

    if (likelySubmitted) {
      standardLoadingBackdrop.open();
    } else {
      standardLoadingBackdrop.close();
    }

    onClose();

    if (shouldVerify) {
      verifyHandler(patientId, activityDefinitionId!, likelySubmitted);
    }
  }

  useEffect(() => {
    if (!open) {
      initializedRef.current = false;
      return;
    }

    if (initializedRef.current || assessmentUrl) {
      return;
    }

    standardLoadingBackdrop.open();

    // Wait for the revisit lookup to resolve for the checklist item that is actually open. Until
    // it does, `latestTask` says nothing about this step and must not be treated as "not found".
    if (isRevisitTask && revisitValidation.isLoading) {
      return;
    }

    if (isRevisitTask && !revisitValidation.latestTask) {
      enqueueSnackbar(
        metadata?.errorMessage ||
          'The screening has not been completed or there are no unanswered questions',
        { variant: 'error' }
      );
      handleClose(false);
      return;
    }

    if (patientId && activityDefinitionId) {
      initializedRef.current = true;
      verifyRunRef.current++;
      initialHandler(patientId, activityDefinitionId);
    }
  }, [
    open,
    patientId,
    activityDefinitionId,
    isRevisitTask,
    revisitValidation.isLoading,
    assessmentUrl,
  ]);

  useEffect(
    () => () => {
      verifyRunRef.current++;

      if (verifyLoaderOpenRef.current) {
        verifyLoaderOpenRef.current = false;
        standardLoadingBackdropRef.current.close();
      }
    },
    []
  );

  useEffect(() => {
    if (standardLoadingBackdrop.forceClose) {
      handleClose(false);
    }
  }, [standardLoadingBackdrop.forceClose]);

  return assessmentUrl ? (
    <AssessmentFrame
      open={open}
      url={assessmentUrl}
      onCancel={handleClose}
      onPageLoad={() => {
        surveyPageLoadsRef.current++;
      }}
    />
  ) : null;
}
